import type { Slide } from '@/framework/types';
import { frames, holdsMs } from './frames.generated';
import './orca.css';

/**
 * The AI Conference booth loop — unattended, 18 frames, about three minutes.
 *
 * Unlike every other booth deck, this one does NOT build from `_booth-base`:
 * it is Orca-branded rather than StreamNative-branded, it runs unattended
 * instead of being presented, and it tells one connected two-product story —
 * the StreamNative Data Platform supplies live governed context, Orca runs
 * agents against it under operator-defined rules. See README.md.
 *
 * It is built for someone who joins midway. Every frame carries its own
 * headline, its own product identity in the footer, and a standing
 * "Booth #133" next step; no frame is a dimmed repeat of another, and none
 * depends on having seen an earlier one.
 *
 * Frames are raw HTML for the same reason as the talk deck — a pixel-exact port
 * of the rendered original. The HTML is a build-time constant generated from our
 * own templates by scratchpad/decks/port.py; nothing here is user input or
 * fetched at runtime.
 */

type Meta = { section: string; notes: readonly string[] };

const ORCA = 'Harness the agents';
const DATA = 'Harness the data';
const BOTH = 'Both';

const META: readonly Meta[] = [
  {
    section: BOTH,
    notes: [
      'The whole story up front: live context from the data platform, agent execution with Orca.',
      'Both products are named on frame 1 — nobody should have to wait to learn what we sell.',
    ],
  },
  {
    section: BOTH,
    notes: [
      'Two named product blocks and the context that flows between them.',
      'Say "use either on its own" — they are not a bundle.',
    ],
  },
  {
    section: DATA,
    notes: [
      'One concrete example of staleness: the account moves to past due at 09:14, the agent is reading the 02:00 snapshot.',
      'Deliberately no blanket claims about warehouses or source systems — those do not survive scrutiny at a booth.',
    ],
  },
  {
    section: DATA,
    notes: ['What live context has to be: fresh, governed, reliable.'],
  },
  {
    section: DATA,
    notes: [
      'Capture, stream, transform, serve — with the products named at each stage and an agent reading the current state.',
      'SQL Workspace carries its Private Preview badge. Do not describe it as available.',
      '"A Streamhouse architecture" is a supporting label. The category is vendor-neutral; we do not speak for the working group.',
    ],
  },
  {
    section: DATA,
    notes: [
      'Pulsar and Kafka on one engine — both services land on Ursa, which writes straight to object storage.',
    ],
  },
  {
    section: DATA,
    notes: [
      'Trusted for mission-critical data. These are data-platform references.',
      'It stays in the data half on purpose, and says "data" not "data and AI", so it cannot read as a list of Orca customers.',
    ],
  },
  {
    section: ORCA,
    notes: [
      'The hinge: live business context goes into Orca, actions come out under operator-defined rules.',
      'This frame is why the second half follows the first.',
    ],
  },
  {
    section: ORCA,
    notes: [
      'Orca Agent Engine is open source, Apache 2.0, Developer Preview.',
      'Any model, any harness, in your environment — the positioning line.',
    ],
  },
  {
    section: ORCA,
    notes: [
      'Run the agent loop, isolate tool execution, keep durable session records.',
      'The deep dive figure: brain, hands and session are decoupled.',
    ],
  },
  {
    section: ORCA,
    notes: [
      'Three harnesses: Claude Agent SDK (the default), Codex SDK, Pi SDK. Models: Anthropic, OpenAI, or any OpenAI-compatible endpoint.',
      'Do not imply every provider works with every harness. Model changes create a version; harness changes create an agent, so switching harness means creating a new one.',
    ],
  },
  {
    section: ORCA,
    notes: [
      'The session record: input, tool activity, approval, outcome — appended outside the worker, which can restart.',
      'Durable history supports recovery. It does NOT make external tool calls exactly-once.',
    ],
  },
  {
    section: ORCA,
    notes: [
      'Organization, workspace, agent, session. Any level can tighten a rule; none can loosen one.',
      'Not "rules only get stricter" — administrators can still revise policy.',
    ],
  },
  {
    section: ORCA,
    notes: [
      'Budget as a runtime policy, checked at a visible policy point.',
      'Enforcement depends on the harness, and a turn-boundary check cannot stop spending already in flight. Orca never picks a cheaper model for you.',
    ],
  },
  {
    section: ORCA,
    notes: [
      'The agent holds a credential id; the gateway resolves it against your vault on the way out.',
      'Scope the promise to gateway-managed credentials. It does not stop an agent disclosing data it can already read.',
    ],
  },
  {
    section: ORCA,
    notes: [
      'Sessions export as OpenTelemetry traces to the backend you already run — metadata-only or full payload.',
      'One way: Orca sends, and never reads your data back.',
      'For SDK compatibility, say "supported Claude Managed Agents operations" and point at the conformance matrix.',
    ],
  },
  {
    section: BOTH,
    notes: [
      'The payoff: both products in one workflow, with a human approval gate and response events flowing back.',
      'It is labelled an example workflow. Do not present it as the live demo.',
      'Holds longer than the rest — four steps take more reading.',
    ],
  },
  {
    section: 'The ask',
    notes: [
      'A next step for each audience: scan for the Orca Developer Preview, or ask for the live-context demo.',
      'The StreamNative Cloud credit is its own block — it is not a way into the hosted Orca preview.',
      'Holds longest: someone has to read it, then scan it.',
    ],
  },
];

if (META.length !== frames.length) {
  throw new Error(
    `ai-conference-booth: ${frames.length} frames but ${META.length} metadata entries — re-run scratchpad/decks/port.py`,
  );
}

export const slides: readonly Slide[] = frames.map((html, i) => ({
  id: i + 1,
  section: META[i].section,
  theme: 'dark',
  notes: META[i].notes,
  // The workflow and the closing ask need longer than a plain frame; the
  // timings live in loop.tmpl.html next to the frames they belong to.
  holdMs: holdsMs[i],
  render: () => (
    <div className="orca-stage" dangerouslySetInnerHTML={{ __html: html }} />
  ),
}));
